/**
 * Component: AIImageGenerationCard
 * Author: Dine AI
 * Date: July 16, 2025
 * Last Modified: December 17, 2025 - Light mode conversion
 *
 * Purpose:
 * - Displays AI image generation status and usage
 * - Shows annual quota usage with visual progress bar
 * - Provides quick access to image generation tools
 * - Handles upgrade prompts for limited plans
 * - Shows menu item image coverage statistics
 *
 * Props:
 * - subscriptionPlan: string - Current subscription tier
 * - menuId?: string | number - Active menu ID
 * - usage?: {used, limit, remaining} - Annual quota usage
 * - menuStats?: Object - Menu image statistics
 * - onGenerateClick?: () => void - Custom click handler
 *
 * State Management:
 * - No internal state (purely presentational)
 * - Receives all data through props
 *
 * Performance:
 * - Memoized calculations for usage percentages
 * - Conditional rendering based on plan features
 * - Framer Motion for smooth animations
 *
 * Error Handling:
 * - Graceful display with missing data
 * - Clear messaging for quota limits
 * - Upgrade path for restricted features
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Image, Sparkles, ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface AIImageGenerationCardProps {
  subscriptionPlan: string;
  menuId?: string | number;
  usage?: {
    used: number;
    limit: number;
    remaining: number;
  };
  menuStats?: {
    totalItems: number;
    itemsWithImages: number;
    itemsWithoutImages: number;
    aiGeneratedImages: number;
    userUploadedImages: number;
  };
  onGenerateClick?: () => void;
}

const AIImageGenerationCard: React.FC<AIImageGenerationCardProps> = ({
  subscriptionPlan,
  menuId,
  usage,
  menuStats,
  onGenerateClick
}) => {
  const navigate = useNavigate();

  // Check if plan includes AI images - ALL plans now have AI images
  const hasAIImages = ['yummy', 'delicious', 'gourmet', 'trial'].includes(subscriptionPlan.toLowerCase());
  const yearlyLimit = subscriptionPlan.toLowerCase() === 'gourmet' ? 300 :
                      subscriptionPlan.toLowerCase() === 'delicious' ? 150 :
                      subscriptionPlan.toLowerCase() === 'yummy' ? 50 :
                      subscriptionPlan.toLowerCase() === 'trial' ? 150 : 0;

  if (!hasAIImages) {
    // Show upgrade prompt for plans without AI images
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <div className="bg-gray-100 p-2 rounded-lg">
            <Image className="h-6 w-6 text-gray-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Image Generation</h3>
            <p className="text-gray-500 mb-4">
              Upgrade to Delicious or Gourmet plan to generate professional AI images for your menu items.
            </p>
            <Button
              className="bg-[#FDB913] hover:bg-[#e5a711] text-black"
              onClick={() => navigate('/plan-selection')}
            >
              Upgrade Now
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  // Calculate progress percentage
  const usagePercentage = usage ? (usage.used / usage.limit) * 100 : 0;
  const isNearLimit = usage && usage.remaining <= 5;
  const isAtLimit = usage && usage.remaining === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className={cn(
        "bg-white rounded-2xl p-4 sm:p-6 border shadow-sm",
        isAtLimit
          ? "border-red-200"
          : "border-gray-200"
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn(
          "p-2 rounded-lg",
          isAtLimit ? "bg-red-100" : "bg-purple-100"
        )}>
          <Image className={cn(
            "h-6 w-6",
            isAtLimit ? "text-red-600" : "text-purple-600"
          )} />
        </div>

        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900">AI Image Generation</h3>
            {isNearLimit && !isAtLimit && (
              <div className="flex items-center gap-1 text-yellow-600 text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>Low quota</span>
              </div>
            )}
          </div>

          {/* Usage Progress Bar */}
          {usage && (
            <div className="mb-3 sm:mb-4">
              <div className="flex justify-between text-xs sm:text-sm mb-1">
                <span className="text-gray-500">{usage.used} of {usage.limit} used</span>
                <span className={cn(
                  "font-medium",
                  isAtLimit ? "text-red-600" :
                  isNearLimit ? "text-yellow-600" :
                  "text-green-600"
                )}>
                  {usage.remaining} left
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={cn(
                    "h-full transition-all duration-500",
                    isAtLimit ? "bg-red-500" :
                    isNearLimit ? "bg-yellow-500" :
                    "bg-gradient-to-r from-purple-500 to-pink-500"
                  )}
                  style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Menu Stats */}
          {menuStats && (
            <div className="flex flex-col sm:grid sm:grid-cols-2 gap-2 mb-4 text-sm">
              <div className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                <span className="text-gray-500 text-xs sm:text-sm">Without Images</span>
                <p className="text-gray-900 font-medium">{menuStats.itemsWithoutImages} items</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                <span className="text-gray-500 text-xs sm:text-sm">AI Generated</span>
                <p className="text-gray-900 font-medium">{menuStats.aiGeneratedImages} items</p>
              </div>
            </div>
          )}

          {/* Action Button */}
          <Button
            className={cn(
              "w-full",
              isAtLimit
                ? "bg-gray-200 hover:bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
            )}
            disabled={isAtLimit}
            onClick={() => {
              if (onGenerateClick) {
                onGenerateClick();
              } else {
                if (menuId) {
                  navigate(`/restaurant-menu-review?processing_id=${menuId}&aiMode=true`);
                } else {
                  navigate('/restaurant-menu-upload');
                }
              }
            }}
          >
            {isAtLimit ? (
              <>
                <AlertCircle className="mr-1 sm:mr-2 h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Annual Limit Reached</span>
                <span className="sm:hidden">Limit Reached</span>
              </>
            ) : (
              <>
                <Sparkles className="mr-1 sm:mr-2 h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Generate AI Images</span>
                <span className="sm:hidden">Generate</span>
                <ArrowRight className="ml-1 sm:ml-2 h-4 w-4 flex-shrink-0" />
              </>
            )}
          </Button>

          {/* Helper Text */}
          <p className="text-gray-500 text-xs mt-2 text-center">
            {isAtLimit
              ? <span className="block sm:inline">Your annual limit resets on January 1st</span>
              : menuStats?.itemsWithoutImages
                ? (
                  <>
                    <span className="hidden sm:inline">Quick generate for {menuStats.itemsWithoutImages} items without images</span>
                    <span className="sm:hidden">Generate for {menuStats.itemsWithoutImages} items</span>
                  </>
                )
                : <span className="block sm:inline">Professional food photography for your menu</span>
            }
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default AIImageGenerationCard;
